import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { Check } from 'lucide-react';
import {
  REST_CHANGE_EVENT,
  readActiveRest,
  readRestOf,
  requestOpenRestExercise,
  restEndsAt,
} from '../../../lib/activeRest';
import { restAlarmKey, ringRestAlarmOnce } from '../../../lib/restAlarm';

const TICK_MS = 250;
// Au-delà, le téléphone était en veille à la fin du repos : pas de sonnerie
// en rallumant l'écran (useSessionRest applique la même règle).
const RING_LATE_MS = 5000;
// « Repos terminé » reste affiché un moment, même sans le son.
const FINISHED_FLASH_MS = 5000;
const FLASH_LATE_MS = 30000;
// L'île s'allonge quelques secondes quand elle apparaît (l'élève vient de
// quitter l'exercice : nom + temps), puis se rétracte en pilule anneau + temps.
const EXPANDED_MS = 3000;

const STROKE = 2.5;

/** Anneau de progression du repos (ratio = part du repos restante). */
const RestRing = ({ size, ratio, color = '#d4845a' }) => {
  const radius = (size - STROKE) / 2;
  const circumference = 2 * Math.PI * radius;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" className="shrink-0 -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth={STROKE} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={STROKE}
        strokeLinecap="round"
        strokeDasharray={`${circumference * ratio} ${circumference}`}
      />
    </svg>
  );
};

// Même île pour le repos en cours et « Repos terminé » : noire, collée au haut
// de l'écran, sous la barre d'état. En bas, elle gênait la navigation.
const ISLAND = 'pointer-events-auto flex items-center rounded-full text-white shadow-[0_10px_30px_rgba(0,0,0,0.55)]';

const formatClock = (seconds) => {
  const total = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

// Le décompte complet (RestTimer) est à l'écran : la capsule s'efface.
const fullTimerOnScreen = () => Boolean(document.querySelector('[data-testid="rest-timer"]'));

/**
 * Capsule du repos en cours, sur toutes les pages élève.
 *
 * Le décompte complet vit dans la modale d'exercice : dès que l'élève la ferme
 * ou change de page (vidéothèque, messages, planning…), il perdait son repos
 * de vue, et l'alarme de fin ne sonnait plus. La capsule prend le relais en
 * haut de l'écran, façon « île » : allongée 3 s (exercice + temps), puis une
 * pilule anneau + temps. Un tap ramène à l'exercice. À la fin, elle sonne
 * (une seule fois, avec l'écran de séance s'il est monté) et affiche
 * « Repos terminé ».
 */
const FloatingRestTimer = () => {
  const { t } = useTranslation(['workout']);
  const navigate = useNavigate();
  const [now, setNow] = useState(() => Date.now());
  const [active, setActive] = useState(() => readActiveRest());
  const [hidden, setHidden] = useState(fullTimerOnScreen);
  const [finished, setFinished] = useState(null); // { sessionId, exerciseIndex, exerciseName, until }
  const trackedRef = useRef(null);
  const finishedUntilRef = useRef(0);
  // Fin de la phase « en grand » : posée chaque fois que la capsule réapparaît.
  const expandedUntilRef = useRef(0);
  const wasVisibleRef = useRef(false);

  const refresh = useCallback(() => {
    const at = Date.now();

    // Fin du repos suivi : terminé (sonner) ou retiré avant la fin (« Passer »).
    const tracked = trackedRef.current;
    if (tracked) {
      const stored = readRestOf(tracked.sessionId);
      const same = stored && stored.startedAt === tracked.rest.startedAt;
      const endsAt = same ? restEndsAt(stored) : tracked.endsAt;
      const done = same ? at >= endsAt : at >= endsAt - 400;
      if (done) {
        trackedRef.current = null;
        const late = at - endsAt;
        if (late < RING_LATE_MS && document.visibilityState === 'visible') {
          ringRestAlarmOnce(restAlarmKey(tracked.sessionId, tracked.rest));
        }
        if (late < FLASH_LATE_MS) {
          finishedUntilRef.current = at + FINISHED_FLASH_MS;
          setFinished({
            sessionId: tracked.sessionId,
            exerciseIndex: tracked.rest.exerciseIndex,
            exerciseName: tracked.rest.exerciseName || '',
            until: at + FINISHED_FLASH_MS,
          });
        }
      } else if (!same) {
        trackedRef.current = null;
      }
    }

    const next = readActiveRest(at);
    if (next) trackedRef.current = next;
    // Sans repos ni « Repos terminé » à l'écran, rien ne bouge : pas de
    // nouveau rendu toutes les 250 ms sur toutes les pages élève.
    const flashing = finishedUntilRef.current > at;
    if (!flashing && finishedUntilRef.current) {
      finishedUntilRef.current = 0;
      setFinished(null);
    }
    setActive(next);
    const fullOnScreen = fullTimerOnScreen();
    const visible = Boolean(next) && !fullOnScreen;
    if (visible && !wasVisibleRef.current) expandedUntilRef.current = at + EXPANDED_MS;
    wasVisibleRef.current = visible;
    if (next || flashing) {
      setNow(at);
      setHidden(fullOnScreen);
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, TICK_MS);
    window.addEventListener(REST_CHANGE_EVENT, refresh);
    window.addEventListener('storage', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      clearInterval(id);
      window.removeEventListener(REST_CHANGE_EVENT, refresh);
      window.removeEventListener('storage', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [refresh]);

  const flash = finished && now < finished.until ? finished : null;

  const openExercise = (sessionId, exerciseIndex) => {
    setFinished(null);
    finishedUntilRef.current = 0;
    if (!requestOpenRestExercise(sessionId, exerciseIndex)) {
      navigate('/student/dashboard', { state: { resumeSessionId: sessionId } });
    }
  };

  let content = null;
  if (flash && !active) {
    content = (
      <motion.button
        key="finished"
        layout
        type="button"
        data-testid="floating-rest-finished"
        onClick={() => openExercise(flash.sessionId, flash.exerciseIndex)}
        className={`${ISLAND} h-[40px] gap-[10px] bg-gradient-to-b from-[#d4845a] to-[#a66745] pl-[6px] pr-[16px]`}
        initial={{ y: -12, opacity: 0, scale: 0.9 }}
        animate={{ y: 0, opacity: 1, scale: [1, 1.06, 1] }}
        exit={{ y: -12, opacity: 0 }}
        transition={{ duration: 0.35, scale: { duration: 0.5, repeat: 2 } }}
      >
        <span className="grid size-[28px] place-content-center rounded-full bg-white/20">
          <Check className="size-[16px]" strokeWidth={3} aria-hidden="true" />
        </span>
        <span className="text-[15px] font-semibold">{t('workout:rest_timer.finished')}</span>
        {flash.exerciseName && (
          <span className="max-w-[120px] truncate text-[13px] text-white/80">{flash.exerciseName}</span>
        )}
      </motion.button>
    );
  } else if (active && !hidden) {
    const { rest, endsAt, sessionId } = active;
    const total = (rest.seconds || 0) + (rest.extraSeconds || 0);
    const remaining = Math.max(0, (endsAt - now) / 1000);
    const ratio = total > 0 ? Math.min(1, remaining / total) : 0;
    const compact = now >= expandedUntilRef.current;
    content = (
      <motion.button
        key="running"
        layout
        type="button"
        data-testid="floating-rest-timer"
        data-compact={compact ? 'true' : 'false'}
        onClick={() => openExercise(sessionId, rest.exerciseIndex)}
        aria-label={t('workout:rest_timer.floating_aria', { name: rest.exerciseName || t('workout:rest_timer.label') })}
        className={`${ISLAND} border border-white/10 bg-black ${
          compact ? 'h-[34px] gap-[7px] pl-[6px] pr-[13px]' : 'h-[40px] w-[min(220px,calc(100vw-32px))] gap-[10px] pl-[8px] pr-[16px]'
        }`}
        initial={{ y: -12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -12, opacity: 0 }}
        transition={{ duration: 0.25, layout: { type: 'spring', stiffness: 420, damping: 36 } }}
      >
        <RestRing size={compact ? 22 : 26} ratio={ratio} />
        {!compact && (
          <span className="min-w-0 flex-1 truncate text-left text-[12px] font-medium uppercase tracking-[0.08em] text-kaiylo-accent">
            {rest.exerciseName || t('workout:rest_timer.label')}
          </span>
        )}
        <span
          data-testid="floating-rest-clock"
          className={`font-semibold tabular-nums ${compact ? 'text-[15px]' : 'text-[17px]'}`}
        >
          {formatClock(remaining)}
        </span>
      </motion.button>
    );
  }

  return createPortal(
    <div
      data-testid="floating-rest-anchor"
      // Le cadre (transparent, toute la largeur) part à 5 px du bord haut,
      // et la capsule reste au même endroit : Safari 26 peint le bord pour
      // teinter sa barre quand un élément fixe sans couleur unie y touche, à
      // chaque tic du repos. Voir PANEL_EDGE_GAP_PX (ExerciseValidationModal).
      className="pointer-events-none fixed inset-x-0 top-[5px] z-[140] flex justify-center px-4"
      style={{ paddingTop: 'max(0px, calc(env(safe-area-inset-top, 0px) - 1px))' }}
    >
      <AnimatePresence>{content}</AnimatePresence>
    </div>,
    document.body
  );
};

export default FloatingRestTimer;
